import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { EventDetailOutputDto, EventsService } from '@smatchy/sdk';
import { catchError, map, of, tap } from 'rxjs';
import {
  AcceptEventInvitationAction,
  DeclineEventInvitationAction,
  GetMyEventInvitationsAction,
  RemoveEventFromInvitationsAction,
} from './my-event-invitations.actions';
import { ShowToastErrorAction } from '../../app';
import { TranslateService } from '@ngx-translate/core';

export interface MyEventInvitationsStateModel {
  invitations: EventDetailOutputDto[];
  loading: boolean;
  processingEventId: string | null;
}

const defaults: MyEventInvitationsStateModel = {
  invitations: [],
  loading: false,
  processingEventId: null,
};

@State<MyEventInvitationsStateModel>({
  name: 'myEventInvitations',
  defaults,
})
@Injectable()
export class MyEventInvitationsState {
  constructor(
    private readonly _eventsService: EventsService,
    private readonly _translate: TranslateService,
  ) {}

  @Action(GetMyEventInvitationsAction)
  getMyEventInvitations({ patchState }: StateContext<MyEventInvitationsStateModel>) {
    patchState({ loading: true });

    return this._eventsService.getMyEventInvitations().pipe(
      map((res) => (res.value?.events ?? []) as EventDetailOutputDto[]),
      tap((invitations) => {
        patchState({
          loading: false,
          invitations,
        });
      }),
      catchError(() => {
        patchState({ loading: false });
        return of(null);
      }),
    );
  }

  @Action(AcceptEventInvitationAction)
  acceptEventInvitation(
    { patchState, dispatch }: StateContext<MyEventInvitationsStateModel>,
    { eventId }: AcceptEventInvitationAction,
  ) {
    patchState({ processingEventId: eventId });

    return this._eventsService.acceptMyInvitation({ id: eventId }).pipe(
      tap((res) => {
        if (res.error) {
          if (res.error === 'event_expired') {
            dispatch(new ShowToastErrorAction(this._translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_EXPIRED')));
          } else if (res.error === 'event_full') {
            dispatch(new ShowToastErrorAction(this._translate.instant('GROUP_INVITATION.ACTIVITY_FULL')));
          }
          patchState({ processingEventId: null });
          return;
        }
        dispatch(new RemoveEventFromInvitationsAction(eventId));
        patchState({ processingEventId: null });
      }),
      catchError(() => {
        patchState({ processingEventId: null });
        return of(null);
      }),
    );
  }

  @Action(DeclineEventInvitationAction)
  declineEventInvitation(
    { patchState, dispatch }: StateContext<MyEventInvitationsStateModel>,
    { eventId }: DeclineEventInvitationAction,
  ) {
    patchState({ processingEventId: eventId });

    return this._eventsService.declineMyInvitation({ id: eventId }).pipe(
      tap(() => {
        dispatch(new RemoveEventFromInvitationsAction(eventId));
        patchState({ processingEventId: null });
      }),
      catchError(() => {
        patchState({ processingEventId: null });
        return of(null);
      }),
    );
  }

  @Action(RemoveEventFromInvitationsAction)
  removeEventFromInvitations(
    { getState, patchState }: StateContext<MyEventInvitationsStateModel>,
    { eventId }: RemoveEventFromInvitationsAction,
  ) {
    const state = getState();
    patchState({
      invitations: state.invitations.filter((event) => event.id !== eventId),
    });
  }
}
